import React from 'react'
import { motion } from 'framer-motion'
import { 
  ShoppingBag, Star, Heart, Eye, ArrowRight, Shield, 
  Clock, MapPin, Zap, TrendingUp, Award, Verified
} from 'lucide-react'

const MarketplacePreview = () => {
  const [selectedCategory, setSelectedCategory] = React.useState('Featured')

  const categories = [
    { name: 'Featured', count: 12, icon: '⭐' },
    { name: 'Guitars', count: 45, icon: '🎸' },
    { name: 'Amplifiers', count: 23, icon: '🔊' },
    { name: 'Drums', count: 18, icon: '🥁' },
    { name: 'Studio Gear', count: 31, icon: '🎧' },
    { name: 'Vintage', count: 16, icon: '✨' }
  ]

  const hotListings = [
    {
      id: 1,
      title: '1965 Fender Stratocaster',
      price: 8500,
      originalPrice: 12000,
      condition: 'excellent',
      seller: {
        name: 'Vintage Mike',
        rating: 4.9,
        verified: true,
        dealCount: 47
      },
      image: 'https://images.pexels.com/photos/1407322/pexels-photo-1407322.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'Guitars',
      brand: 'Fender',
      views: 1234,
      likes: 89,
      timeLeft: '2 days',
      featured: true,
      badges: ['vintage', 'rare'],
      description: 'Immaculate vintage Strat with original case and paperwork'
    },
    {
      id: 2,
      title: 'Neumann U87 Microphone',
      price: 2800,
      condition: 'like-new',
      seller: {
        name: 'Studio Pro Audio',
        rating: 5.0,
        verified: true,
        dealCount: 156
      },
      image: 'https://images.pexels.com/photos/164938/pexels-photo-164938.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'Studio Gear',
      brand: 'Neumann',
      views: 567,
      likes: 43,
      timeLeft: '5 hours',
      badges: ['professional', 'studio-grade']
    },
    {
      id: 3,
      title: 'Marshall JCM800 Half Stack',
      price: 2200,
      originalPrice: 2800,
      condition: 'excellent',
      seller: {
        name: 'Rock City Gear',
        rating: 4.8,
        verified: true,
        dealCount: 89
      },
      image: 'https://images.pexels.com/photos/1407322/pexels-photo-1407322.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'Amplifiers',
      brand: 'Marshall',
      views: 789,
      likes: 67,
      timeLeft: '1 day',
      badges: ['classic', 'gigged']
    },
    {
      id: 4,
      title: 'DW Collector\'s Series Kit',
      price: 3500,
      condition: 'good',
      seller: {
        name: 'Rhythm Section',
        rating: 4.7,
        verified: false,
        dealCount: 23
      },
      image: 'https://images.pexels.com/photos/164743/pexels-photo-164743.jpeg?auto=compress&cs=tinysrgb&w=800',
      category: 'Drums',
      brand: 'DW',
      views: 445,
      likes: 32,
      timeLeft: '3 days',
      badges: ['complete-kit']
    }
  ]

  const conditionColors = {
    'like-new': 'condition-new',
    'excellent': 'condition-excellent', 
    'good': 'condition-good',
    'fair': 'condition-fair',
    'poor': 'condition-poor'
  }

  const badgeColors = {
    vintage: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
    rare: 'bg-red-500/20 text-red-300 border-red-500/30',
    professional: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
    'studio-grade': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
    classic: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    gigged: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
    'complete-kit': 'bg-green-500/20 text-green-300 border-green-500/30'
  }

  return (
    <section className="section-padding py-24 bg-gradient-to-b from-le-darker/30 to-transparent">
      <div className="container-max">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-white mb-6">
            Gear
            <span className="text-gradient block">Marketplace</span>
          </h2>
          <p className="text-xl text-white/70 max-w-4xl mx-auto mb-8">
            Premium music equipment from trusted local sellers. Every transaction protected, 
            every piece authenticated, every deal transparent.
          </p>
          
          {/* Marketplace Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {[
              { label: 'Active Listings', value: '200+', icon: ShoppingBag },
              { label: 'Verified Sellers', value: '85%', icon: Shield },
              { label: 'Monthly Sales', value: '$50K+', icon: TrendingUp },
              { label: 'Avg Rating', value: '4.8★', icon: Award }
            ].map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  className="glass-card p-4 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Icon size={24} className="text-le-accent mx-auto mb-2" />
                  <div className="text-2xl font-bold text-white">{stat.value}</div>
                  <div className="text-xs text-white/60">{stat.label}</div>
                </motion.div>
              )
            })}
          </div>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          className="flex space-x-2 mb-12 overflow-x-auto pb-2"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {categories.map((category, index) => (
            <motion.button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`flex items-center space-x-3 px-6 py-4 rounded-xl font-medium whitespace-nowrap transition-all duration-300 ${
                selectedCategory === category.name
                  ? 'bg-gradient-to-r from-le-accent to-purple-600 text-white shadow-lg shadow-le-accent/30'
                  : 'glass-card text-white/70 hover:text-white hover:bg-white/10'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="text-xl">{category.icon}</span>
              <div>
                <div>{category.name}</div>
                <div className="text-xs opacity-70">{category.count} items</div>
              </div>
            </motion.button>
          ))}
        </motion.div>

        {/* Hot Listings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {hotListings.map((listing, index) => (
            <motion.div
              key={listing.id}
              className="glass-card overflow-hidden card-hover group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
            >
              {listing.featured && (
                <div className="absolute top-4 left-4 z-10 bg-gradient-to-r from-le-accent to-le-accent-2 text-white text-xs px-3 py-2 rounded-full font-bold">
                  HOT DEAL
                </div>
              )}

              <div className="relative h-56 overflow-hidden">
                <img
                  src={listing.image}
                  alt={listing.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                {/* Quick Actions */}
                <div className="absolute top-4 right-4 flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="w-8 h-8 glass-card rounded-full flex items-center justify-center text-white hover:text-red-400 transition-colors duration-200">
                    <Heart size={14} />
                  </button>
                </div>

                {/* Time Left Badge */}
                <div className="absolute bottom-4 right-4 bg-red-500/80 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full font-bold flex items-center space-x-1">
                  <Clock size={12} />
                  <span>{listing.timeLeft} left</span>
                </div>

                {/* Category Badge */}
                <div className="absolute bottom-4 left-4 bg-black/60 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full border border-white/20">
                  {listing.category}
                </div>
              </div>

              <div className="p-6">
                {/* Item Title & Brand */}
                <h3 className="text-lg font-display font-bold text-white mb-2 group-hover:text-le-accent transition-colors duration-300 leading-tight">
                  {listing.title}
                </h3>
                <p className="text-sm text-white/60 mb-3">{listing.brand}</p>

                {/* Badges */}
                {listing.badges && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {listing.badges.map((badge) => (
                      <span
                        key={badge}
                        className={`text-xs px-2 py-1 rounded-full border ${badgeColors[badge] || 'bg-white/10 text-white/70'}`}
                      >
                        {badge.replace('-', ' ')}
                      </span>
                    ))}
                  </div>
                )}

                {/* Seller Info */}
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-le-accent to-le-accent-2 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">
                      {listing.seller.name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-white text-sm">{listing.seller.name}</span>
                      {listing.seller.verified && (
                        <Shield size={14} className="text-le-accent" />
                      )}
                    </div>
                    <div className="flex items-center space-x-3 text-xs text-white/60">
                      <div className="flex items-center space-x-1">
                        <Star size={10} className="text-yellow-400 fill-current" />
                        <span>{listing.seller.rating}</span>
                      </div>
                      <span>{listing.seller.dealCount} deals</span>
                    </div>
                  </div>
                </div>

                {/* Condition */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs px-3 py-1 rounded-full font-medium border ${conditionColors[listing.condition]}`}>
                    {listing.condition.replace('-', ' ').toUpperCase()}
                  </span>
                  <div className="flex items-center space-x-3 text-xs text-white/60">
                    <div className="flex items-center space-x-1">
                      <Eye size={12} />
                      <span>{listing.views.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Heart size={12} />
                      <span>{listing.likes}</span>
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="text-2xl font-bold text-le-accent">
                      ${listing.price.toLocaleString()}
                    </div>
                    {listing.originalPrice && (
                      <div className="text-white/40 text-lg line-through">
                        ${listing.originalPrice.toLocaleString()}
                      </div>
                    )}
                  </div>
                  {listing.originalPrice && (
                    <div className="text-green-400 text-sm font-medium">
                      Save ${(listing.originalPrice - listing.price).toLocaleString()}
                      ({Math.round(((listing.originalPrice - listing.price) / listing.originalPrice) * 100)}% off)
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button className="flex-1 btn-primary text-sm py-3">
                    Buy Now
                  </button>
                  <button className="flex-1 btn-secondary text-sm py-3">
                    Make Offer
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Marketplace Features */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {[
            {
              icon: Shield,
              title: 'Secure Transactions',
              description: 'Every purchase protected with escrow and dispute resolution',
              color: 'from-green-500 to-emerald-600'
            },
            {
              icon: Verified,
              title: 'Verified Sellers',
              description: 'ID verification and rating system for trusted transactions',
              color: 'from-blue-500 to-cyan-600'
            },
            {
              icon: Zap,
              title: 'Fast & Fair',
              description: 'Low 3% fees, instant notifications, same-day local pickup',
              color: 'from-purple-500 to-pink-600'
            }
          ].map((feature, index) => {
            const Icon = feature.icon
            return (
              <motion.div
                key={feature.title}
                className="glass-card p-8 text-center card-hover group"
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300, damping: 15 }}
              >
                <div className={`w-20 h-20 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <Icon size={36} className="text-white" />
                </div>
                <h3 className="text-xl font-display font-bold text-white mb-4 group-hover:text-le-accent transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-white/70 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            )
          })}
        </motion.div>

        {/* CTA Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="glass-card p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-le-accent/10 via-transparent to-le-accent-2/10"></div>
            <div className="relative z-10">
              <h3 className="text-3xl font-display font-bold text-white mb-4">
                Ready to Buy or Sell Gear?
              </h3>
              <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
                Join hundreds of musicians trading quality equipment with transparent pricing and secure transactions.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button className="btn-primary text-xl px-12 py-6 flex items-center space-x-3">
                  <ShoppingBag size={24} />
                  <span>Browse All Listings</span>
                </button>
                <button className="btn-accent text-xl px-12 py-6 flex items-center space-x-3">
                  <TrendingUp size={24} />
                  <span>List Your Gear</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default MarketplacePreview